import { IInventoryNode, IPackagingLayer } from "./inventoryTree";

// Per ticket §3.3 "Bulk-upload nudge" (OI-05): the default threshold above
// which the modal should recommend the bulk file upload instead.
export const BULK_UPLOAD_NUDGE_THRESHOLD = 50;

export function buildInventorySkeleton(layers: IPackagingLayer[]): IInventoryNode {
  const build = (idx: number): IInventoryNode => {
    const layer = layers[idx];
    const isBase = idx === layers.length - 1;
    return {
      level: idx + 1,
      packagingType: layer.packaging_type,
      serial: "",
      touched: false,
      collapsed: false,
      children: isBase
        ? undefined
        : Array.from({ length: layer.containment_quantity || 0 }, () => build(idx + 1)),
    };
  };
  return build(0);
}

// Recursive — every slot at every level ("0 of 40").
export function globalProgress(node: IInventoryNode): { filled: number; total: number } {
  let filled = node.serial.trim() ? 1 : 0;
  let total = 1;
  (node.children || []).forEach((child) => {
    const g = globalProgress(child);
    filled += g.filled;
    total += g.total;
  });
  return { filled, total };
}

// This node's own slot + its DIRECT children's slots only ("0 of 13" for a pack section).
export function sectionProgress(node: IInventoryNode): { filled: number; total: number } {
  const childrenFilled = (node.children || []).filter((child) => child.serial.trim()).length;
  return {
    filled: (node.serial.trim() ? 1 : 0) + childrenFilled,
    total: 1 + (node.children?.length || 0),
  };
}

export function hasDuplicateSerial(node: IInventoryNode, seen: Set<string> = new Set()): boolean {
  const serial = node.serial.trim();
  if (serial) {
    if (seen.has(serial)) return true;
    seen.add(serial);
  }
  return (node.children || []).some((child) => hasDuplicateSerial(child, seen));
}

export function hasFormatError(node: IInventoryNode): boolean {
  if (node.error) return true;
  return (node.children || []).some((child) => hasFormatError(child));
}

// Trailing-numeric-suffix increment, zero-padding-preserving. If no numeric
// tail is found, only the starting slot can be filled — caller fills slot 1
// and warns; the rest stays empty for manual/scan/paste entry.
export function fillRangeSerials(startSerial: string, count: number): string[] {
  const match = startSerial.match(/^(.*?)(\d+)(\D*)$/);
  if (!match) return [startSerial];

  const [, prefix, digits, suffix] = match;
  const width = digits.length;
  const start = parseInt(digits, 10);
  return Array.from({ length: count }, (_, i) => `${prefix}${String(start + i).padStart(width, "0")}${suffix}`);
}

export interface IParsedPaste {
  values: string[];
  overcount: number;
  multiColumn: boolean;
}

// Delimiter split (newline/comma) + overcount/multi-column handling. Tab
// characters (likely a multi-column spreadsheet copy) reject the whole paste
// so the caller can prompt for a single column, per the ticket's spec.
export function parsePastedSerials(text: string, remainingSlotCount: number): IParsedPaste {
  const lines = text
    .split(/\r\n|\r|\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const multiColumn = lines.some((line) => line.includes("\t"));
  if (multiColumn) {
    return { values: [], overcount: 0, multiColumn: true };
  }

  const tokens = lines
    .flatMap((line) => line.split(","))
    .map((token) => token.trim())
    .filter(Boolean);

  return {
    values: tokens.slice(0, remainingSlotCount),
    overcount: Math.max(0, tokens.length - remainingSlotCount),
    multiColumn: false,
  };
}

export function toSubmitTree(node: IInventoryNode): Record<string, unknown> {
  return {
    serial: node.serial.trim(),
    ...(node.children ? { children: node.children.map(toSubmitTree) } : {}),
  };
}
